'use client';

import { useEffect, useState } from 'react';

import {
  animated as an,
  useSpring,
  type UseSpringProps,
} from '@react-spring/web';

function useAnimatedPath({
  toggle,
  delay = 0,
  config,
}: { toggle: boolean } & Pick<UseSpringProps, 'delay' | 'config'>) {
  // It does not work with 0. Must be null
  const [length, setLength] = useState<number>(null as unknown as number);
  const animatedStyle = useSpring({
    strokeDashoffset: toggle ? 0 : length,
    strokeDasharray: length,
    delay,
    config,
  });

  return {
    style: animatedStyle,
    ref: (ref: SVGPathElement | null) => {
      // The ref is `null` on component unmount
      if (ref) {
        setLength(ref.getTotalLength());
      }
    },
  };
}

export function HandDrawnArrow({ className }: { className?: string }) {
  const [toggle, setToggle] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setToggle(true);
    }, 0);
  }, []);

  return (
    <svg
      width="41mm"
      height="104mm"
      version="1.1"
      viewBox="0 0 41 104"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke="rgb(var(--brand, 253 212 2))"
      className={className}
      strokeLinecap="round"
    >
      <Line toggle={toggle} />
      <Arrow toggle={toggle} />
      <Wrinkle1 toggle={toggle} />
      <Wrinkle2 toggle={toggle} />
    </svg>
  );
}

function Line({ toggle }: { toggle: boolean }) {
  const animationProps = useAnimatedPath({
    toggle,
    config: { tension: 50 },
  });
  return (
    <an.path
      {...animationProps}
      d="m28.467 103.25c-22.043-19.624-16.018-34.467-2.2737-42.402 15.132-8.7365 18.103-19.922 9.641-31.519-5.9577-8.1649-14.98-4.0831-12.066 0.96406 4.8701 8.4353 20.809-10.815 0.25703-22.726-6.663-3.8615-10.689-4.2762-20.458-4.2719"
    />
  );
}

function Arrow({ toggle }: { toggle: boolean }) {
  const animationProps = useAnimatedPath({
    toggle,
    delay: 1200,
    config: { tension: 50 },
  });
  return (
    <an.path
      {...animationProps}
      d="m4.6421 0.78511-3.7618 2.687 3.7202 2.582"
      strokeLinejoin="round"
    />
  );
}

/**
 * Upper wrinkle
 */
function Wrinkle1({ toggle }: { toggle: boolean }) {
  const animationProps = useAnimatedPath({
    toggle,
    delay: 800,
    config: { tension: 250 },
  });
  return (
    <an.path
      {...animationProps}
      d="m34.788 16.191c-1.5127-2.8252-3.2976-5.2634-5.3929-6.8514"
    />
  );
}

/**
 * Lower wrinkle
 */
function Wrinkle2({ toggle }: { toggle: boolean }) {
  const animationProps = useAnimatedPath({
    toggle,
    delay: 100,
    config: { tension: 250 },
  });
  return (
    <an.path
      {...animationProps}
      d="m12.836 83.725c-1.1251-4.5795-0.72207-9.5239 2.1533-14.44"
    />
  );
}
